import React, { useState, useEffect, useMemo } from "react";
import "./TrainingReport.scss";
import { Breadcrumb } from "components";
import api from "components/api/api";
import {
  Search,
  Filter,
  Download,
  Calendar,
  TrendingUp,
  Award,
  Clock,
  Users,
  PlayCircle,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  PieChart,
} from "lucide-react";

const TrainingReport = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [loading, setLoading] = useState(true);

  const userRole = (localStorage.getItem("userRole") || "").toLowerCase();

  // Data states
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [videoEngagement, setVideoEngagement] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [regionNames, setRegionNames] = useState([]);

  const isAdmin = userRole === "admin";

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch training materials
        const materialsRes = await api.fetchAllTrainings();
        setTrainingMaterials(materialsRes.data.data || []);

        // Fetch video engagement
        const engagementRes = await api.fetchVideoEngagement();
        setVideoEngagement(
          Array.isArray(engagementRes.data?.data) ? engagementRes.data.data : []
        );

        // Fetch users for regions
        const usersRes = await api.fetchUsers();
        const users = usersRes.data?.data || [];
        const regions = [
          "all",
          ...new Set(users.map((u) => u.region).filter(Boolean)),
        ];
        setRegionNames(regions);

        // Mock assignments data (replace with actual API call)
        setAssignments([
          // This should come from your actual API
        ]);
      } catch (error) {
        console.error("Error fetching report data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate statistics

  const stats = useMemo(() => {
    const totalMaterials = trainingMaterials.length;

    const totalEngagements = videoEngagement.length;

    const completedCount = videoEngagement.filter(
      (e) => e.status === "completed"
    ).length;

    const inProgressCount = videoEngagement.filter(
      (e) => e.status === "in-progress"
    ).length;

    const avgProgress =
      totalEngagements > 0
        ? Math.round(
            videoEngagement.reduce((sum, e) => sum + (e.progress || 0), 0) /
              totalEngagements
          )
        : 0;

    const avgCompletionRate =
      totalEngagements > 0
        ? Math.round((completedCount / totalEngagements) * 100)
        : 0;

    return {
      totalMaterials,
      completedCount,
      inProgressCount,
      avgCompletionRate,
      avgProgress,
    };
  }, [trainingMaterials, videoEngagement]);

  // Filter materials based on search and filters
  const filteredMaterials = useMemo(() => {
    return trainingMaterials.filter((material) => {
      const matchesSearch =
        material.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        material.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const materialRegion = (material.region || "").toLowerCase();
      const selectedReg = selectedRegion.toLowerCase();

      const matchesRegion =
        selectedReg === "all" ||
        materialRegion === selectedReg ||
        materialRegion === "all";
      const matchesType =
        selectedType === "all" || material.type === selectedType;

      // Status filter based on completion rate
      let matchesStatus = true;
      if (selectedStatus === "completed") {
        matchesStatus = (material.completionRate || 0) >= 90;
      } else if (selectedStatus === "in-progress") {
        matchesStatus =
          (material.completionRate || 0) > 0 &&
          (material.completionRate || 0) < 90;
      } else if (selectedStatus === "not-started") {
        matchesStatus = (material.completionRate || 0) === 0;
      }

      return matchesSearch && matchesRegion && matchesType && matchesStatus;
    });
  }, [
    trainingMaterials,
    searchQuery,
    selectedRegion,
    selectedType,
    selectedStatus,
  ]);

  // Filter engagement data
  const filteredEngagement = useMemo(() => {
    return videoEngagement.filter((engagement) => {
      const matchesSearch =
        engagement.learner?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        engagement.video?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        selectedStatus === "all" || engagement.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [videoEngagement, searchQuery, selectedStatus]);

  // Export report functionality
  const exportReport = () => {
    // Create CSV content
    const headers = [
      "Training Title",
      "Type",
      "Region",
      "Duration",
      "Assigned To",
      "Completion Rate",
      "Upload Date",
    ];

    const rows = filteredMaterials.map((material) => [
      material.title,
      material.type,
      material.region,
      material.duration,
      material.assignedTo || 0,
      `${material.completionRate || 0}%`,
      material.uploadDate
        ? new Date(material.uploadDate).toLocaleDateString()
        : "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `training-report-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={18} color="#27ae60" />;
      case "in-progress":
        return <AlertCircle size={18} color="#f39c12" />;
      default:
        return <XCircle size={18} color="#e74c3c" />;
    }
  };

  const getStatusClass = (completionRate) => {
    if (completionRate >= 90) return "status-completed";
    if (completionRate > 0) return "status-in-progress";
    return "status-not-started";
  };

  if (loading) {
    return (
      <div className="training-report">
        <Breadcrumb title="Training Report" />
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="training-report">
      <Breadcrumb title="Training Report" />

      {/* Header with Stats */}
      <div className="report-header">
        <div className="header-content">
          <div className="header-title-row">
            <div>
              <h1 className="page-title">Training Analytics & Reports</h1>
              <p className="page-subtitle">
                Comprehensive overview of training materials, assignments, and
                learner progress
              </p>
            </div>
            <button className="export-btn" onClick={exportReport}>
              <Download size={20} />
              Export Report
            </button>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">
              <FileText size={28} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.totalMaterials}</span>
              <span className="stat-label">Total Materials</span>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon">
              <CheckCircle size={28} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.completedCount}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">
              <Clock size={28} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.inProgressCount}</span>
              <span className="stat-label">In Progress</span>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon">
              <Award size={28} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.avgCompletionRate}%</span>
              <span className="stat-label">Avg Completion</span>
            </div>
          </div>

          <div className="stat-card accent">
            <div className="stat-icon">
              <TrendingUp size={28} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.avgProgress}%</span>
              <span className="stat-label">Avg Progress</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search training materials or learners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} />
              <span>Type</span>
            </div>
            <div className="filter-buttons">
              {["all", "video", "document"].map((type) => (
                <button
                  key={type}
                  className={`filter-btn ${
                    selectedType === type ? "active" : ""
                  }`}
                  onClick={() => setSelectedType(type)}
                >
                  {type === "all" ? "All" : type}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} />
              <span>Status</span>
            </div>
            <div className="filter-buttons">
              {["all", "completed", "in-progress", "not-started"].map(
                (status) => (
                  <button
                    key={status}
                    className={`filter-btn ${
                      selectedStatus === status ? "active" : ""
                    }`}
                    onClick={() => setSelectedStatus(status)}
                  >
                    {status === "all"
                      ? "All"
                      : status
                          .replace("-", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </button>
                )
              )}
            </div>
          </div>

          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} />
              <span>Region</span>
            </div>
            <div className="filter-buttons">
              {regionNames.map((region) => (
                <button
                  key={region}
                  className={`filter-btn ${
                    selectedRegion === region ? "active" : ""
                  }`}
                  onClick={() => setSelectedRegion(region)}
                >
                  {region === "all" ? "All Regions" : region}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="report-content">
        {/* Training Materials Overview */}
        <div className="report-section">
          <div className="section-header">
            <div className="section-title">
              <BarChart3 size={24} />
              <h2>Training Materials Overview</h2>
            </div>
            <span className="section-count">
              {filteredMaterials.length} materials
            </span>
          </div>

          <div className="materials-table">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Region</th>
                  <th>Duration</th>
                  <th>Assigned</th>
                  <th>Completion</th>
                  <th>Upload Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.length > 0 ? (
                  filteredMaterials.map((material) => (
                    <tr key={material.id}>
                      <td className="material-title">
                        <div className="title-cell">
                          {material.type === "video" ? (
                            <PlayCircle size={18} color="#667eea" />
                          ) : (
                            <FileText size={18} color="#667eea" />
                          )}
                          <span>{material.title}</span>
                        </div>
                      </td>
                      <td>
                        <span className="type-badge">{material.type}</span>
                      </td>
                      <td>{material.region}</td>
                      <td>{material.duration}</td>
                      <td className="text-center">
                        {material.assignedTo || 0}
                      </td>
                      <td className="text-center">
                        <div className="completion-cell">
                          <div className="completion-bar-small">
                            <div
                              className="completion-fill"
                              style={{
                                width: `${material.completionRate || 0}%`,
                              }}
                            ></div>
                          </div>
                          <span>{material.completionRate || 0}%</span>
                        </div>
                      </td>

                      <td>
                        {material.uploadDate
                          ? new Date(material.uploadDate).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No training materials found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Learner Engagement */}
        {isAdmin && (
          <div className="report-section">
            <div className="section-header">
              <div className="section-title">
                <PieChart size={24} />
                <h2>Learner Engagement</h2>
              </div>
              <span className="section-count">
                {filteredEngagement.length} engagements
              </span>
            </div>

            <div className="engagement-grid">
              {filteredEngagement.length > 0 ? (
                filteredEngagement.map((engagement) => (
                  <div key={engagement.id} className="engagement-card">
                    <div className="engagement-header">
                      <div className="learner-info">
                        <div className="learner-avatar">
                          {engagement.learner?.charAt(0) || "?"}
                        </div>
                        <div>
                          <h4>{engagement.learner}</h4>
                          <p>{engagement.video}</p>
                        </div>
                      </div>
                      {getStatusIcon(engagement.status)}
                    </div>

                    <div className="engagement-progress">
                      <div className="progress-bar-full">
                        <div
                          className="progress-fill"
                          style={{ width: `${engagement.progress}%` }}
                        ></div>
                      </div>
                      <div className="progress-info">
                        <span className="progress-value">
                          {engagement.progress}%
                        </span>
                        <span className={`status-text ${engagement.status}`}>
                          {engagement.status.replace("-", " ")}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data-card">
                  <AlertCircle size={48} color="#95a5a6" />
                  <p>No engagement data available</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingReport;
